/**
 * Plan satırı ekleme / düzenleme penceresi.
 *
 * Malzeme kodları YALNIZ listeden seçilir, elle yazılamaz: model bilinmeyen
 * bir kodu reddetmiyor, sessizce kabul ediyor; o satır stok/talep hesabına
 * girmediği için sonuç yanıltıcı oluyor.
 *
 * Görünüm ayarları (dolgu, başlık) `sim-modal` sınıfıyla Simulation.css'te —
 * uygulamadaki global modal kurallarını bu pencere için geri alıyor.
 */
import { Form, InputNumber, Modal, Segmented, Select } from "antd";
import { PauseCircleOutlined } from "@ant-design/icons";
import { Trans, useTranslation } from "react-i18next";
import "./Simulation.css";

/** Seçenek listesinde renk noktası — plan tablosundaki renk etiketleriyle aynı aile. */
const RENK_NOKTASI = { Beyaz: "#d9dde3", Silver: "#3b82f6", "Cast Iron": "#ea580c" };

/**
 * Aynı adı taşıyan birden çok kod var (üç ayrı "Beyaz U Gövde"), bu yüzden
 * kod da yazılır — solda ad, sağda soluk kod. Arama ikisinde birden çalışır.
 */
const secenekler = (liste, renkAlani) =>
  (liste ?? []).map((m) => {
    const renk = m[renkAlani]?.description;
    return {
      value: Number(m.malzeme_kodu),
      ara: `${m.tanim} ${m.malzeme_kodu}`,
      label: (
        <div className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-line-strong"
              style={{ background: RENK_NOKTASI[renk] ?? "transparent" }}
            />
            <span className="truncate">{m.tanim}</span>
          </span>
          <span className="shrink-0 text-[12px] tabular-nums text-content-faint">
            {m.malzeme_kodu}
          </span>
        </div>
      ),
    };
  });

/** Arama dilden bağımsız olarak Türkçe kurallarla küçültülür (İ/ı). */
const filtre = (girdi, secenek) =>
  String(secenek?.ara ?? "")
    .toLocaleLowerCase("tr")
    .includes(girdi.toLocaleLowerCase("tr"));

export default function SatirModal({
  acik,
  satir,
  baslik,
  govdeListe,
  govdeOnListe,
  onKaydet,
  onVazgec,
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const tur = Form.useWatch("tur", form) ?? (satir?.durus ? "durus" : "siparis");
  const durus = tur === "durus";

  const turler = [
    { value: "siparis", label: t("simulation.order") },
    { value: "durus", label: t("simulation.stop") },
  ];

  // Pencere kapanınca içerik yok ediliyor (destroyOnClose); her açılışta form
  // yeniden kurulduğu için başlangıç değerleri doğrudan buradan okunur.
  const baslangic = {
    tur: satir?.durus ? "durus" : "siparis",
    govde: satir?.durus ? undefined : satir?.govde ?? undefined,
    govde_on: satir?.durus ? undefined : satir?.govde_on ?? undefined,
    uretim: satir?.durus ? undefined : satir?.uretim || undefined,
  };

  const kaydet = async () => {
    const v = await form.validateFields();
    onKaydet(
      v.tur === "durus"
        ? { durus: true, govde: null, govde_on: null, uretim: 0 }
        : { durus: false, govde: v.govde, govde_on: v.govde_on, uretim: v.uretim }
    );
  };

  return (
    <Modal
      open={acik}
      title={baslik}
      okText={t("simulation.apply")}
      cancelText={t("simulation.cancel")}
      onOk={kaydet}
      onCancel={onVazgec}
      className="sim-modal"
      width={520}
      centered
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={baslangic}
        requiredMark={false}
      >
        <Form.Item name="tur">
          <Segmented block options={turler} />
        </Form.Item>

        {durus ? (
          // Duruşta alan göstermek yerine ne olacağı söylenir.
          <div className="flex items-start gap-3 rounded-lg border border-line bg-surface-sunken px-4 py-3">
            <PauseCircleOutlined className="mt-0.5 text-[18px] text-content-muted" />
            <div className="text-[13px] leading-relaxed text-content-muted">
              <Trans i18nKey="simulation.stopNote" components={{ b: <b className="text-content" /> }} />
            </div>
          </div>
        ) : (
          <>
            <Form.Item
              name="govde"
              label={t("simulation.cols.body")}
              rules={[{ required: true, message: t("simulation.validation.body") }]}
            >
              <Select
                showSearch
                placeholder={t("simulation.searchPlaceholder")}
                options={secenekler(govdeListe, "body_color")}
                filterOption={filtre}
                listHeight={300}
              />
            </Form.Item>

            <Form.Item
              name="govde_on"
              label={t("simulation.cols.frontBody")}
              rules={[{ required: true, message: t("simulation.validation.frontBody") }]}
            >
              <Select
                showSearch
                placeholder={t("simulation.searchPlaceholder")}
                options={secenekler(govdeOnListe, "front_body_color")}
                filterOption={filtre}
                listHeight={300}
              />
            </Form.Item>

            <Form.Item
              name="uretim"
              label={t("simulation.cols.qty")}
              rules={[{ required: true, message: t("simulation.validation.qty") }]}
            >
              <InputNumber
                min={1}
                precision={0}
                placeholder={t("simulation.qtyPlaceholder")}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}
